import React, { useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";

import { useMaterialUIController } from "context";

const SCRIPT_CODE = `<script src=\"https://rieshim.app/widget.js\" chatbot-id=\"VÁŠ_CHATBOT_TOKEN\"></script>`;

const steps = [
  {
    label: "Vytvorte si chatbota",
    text: "Prejdite do sekcie Správa chatbotov a kliknite na Vytvoriť chatbota.",
  },
  {
    label: "Pomenujte chatbota",
    text: "Zadajte meno a potvrďte vytvorenie.",
  },
  {
    label: "Konfigurácia",
    text: "Po vytvorení budete automaticky presmerovaný do Konfigurátora, kde môžete upraviť meno, farbu, inštrukcie a vedomosti pre vášho asistenta.",
  },
  {
    label: "Získajte token",
    text: "Po uložení konfigurácie sa vám zobrazí token vášho chatbota.",
  },
  {
    label: "Pripravte si kód",
    text: (
      <>
        Skopírujte nasledujúci kód a <b>nahraďte</b>{" "}
        <span style={{ color: "#1976d2", fontWeight: "bold" }}>VÁŠ_CHATBOT_TOKEN</span> za váš
        token.
      </>
    ),
  },
  {
    label: "Vložte kód na web",
    text: (
      <>
        Vložte tento kód <b>tesne pred ukončovací tag</b>{" "}
        <span style={{ color: "#1976d2", fontWeight: "bold" }}>&lt;/body&gt;</span> na vašom webe.
        <br />
        <span style={{ color: "#757575", fontSize: 14 }}>
          (Neodporúčame vkladať do <b>&lt;head&gt;</b>, pretože widget je vizuálny komponent a
          nemusí sa správne zobraziť, ak sa načíta príliš skoro.)
        </span>
      </>
    ),
  },
];

function Documentation() {
  const [snackbar, setSnackbar] = useState({ open: false, color: "success", message: "" });
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        '<script src="https://rieshim.app/widget.js" chatbot-id="VÁŠ_CHATBOT_TOKEN"></script>'
      );
      setSnackbar({ open: true, color: "success", message: "Kód bol skopírovaný do schránky." });
    } catch {
      setSnackbar({ open: true, color: "error", message: "Nepodarilo sa skopírovať kód." });
    }
  };
  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <MDTypography variant="h3" fontWeight="bold" mb={4} color="primary">
          Ako pridať chatbota na váš web
        </MDTypography>
        <MDBox display="flex" flexDirection="column" gap={2} mb={4}>
          {steps.map((step, idx) => (
            <MDBox
              key={idx}
              display="flex"
              alignItems="flex-start"
              sx={(theme) => ({
                background: darkMode
                  ? idx % 2 === 0
                    ? theme.palette.background.paper
                    : theme.functions.rgba(theme.palette.white.main, 0.05)
                  : idx % 2 === 0
                  ? "#f5f7fa"
                  : "#e3e8f0",
                borderRadius: 2,
                p: 2.2,
                boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
              })}
            >
              <MDBox
                sx={{
                  minWidth: 36,
                  minHeight: 36,
                  background: "#1976d2",
                  color: "#fff",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 18,
                  mr: 2.5,
                  boxShadow: "0 1px 4px 0 rgba(25,118,210,0.10)",
                }}
              >
                {idx + 1}
              </MDBox>
              <MDBox>
                <MDTypography variant="subtitle1" fontWeight="bold" mb={0.5}>
                  {step.label}
                </MDTypography>
                <MDTypography variant="body1" color="text">
                  {step.text}
                </MDTypography>
              </MDBox>
            </MDBox>
          ))}
        </MDBox>
        <MDButton
          variant="outlined"
          color="info"
          size="small"
          onClick={handleCopy}
          sx={{ mb: 1, alignSelf: "flex-start" }}
        >
          Skopírovať kód
        </MDButton>
        <SyntaxHighlighter
          language="html"
          style={oneDark}
          customStyle={{
            borderRadius: 8,
            fontSize: 16,
            margin: 0,
            padding: "18px 24px",
            background: "#23272e",
            width: "100%",
            boxSizing: "border-box",
          }}
          showLineNumbers={false}
          wrapLongLines={true}
        >
          {`<script src="https://rieshim.app/widget.js" chatbot-id="VÁŠ_CHATBOT_TOKEN"></script>`}
        </SyntaxHighlighter>
        <MDBox mt={4}>
          <MDTypography variant="body2" color="text">
            Po vložení a obnovení stránky sa na vašom webe zobrazí widget chatbota. Ďalšie možnosti
            konfigurácie budú postupne pribúdať v sekcii konfigurátora.
          </MDTypography>
        </MDBox>
      </MDBox>
      <MDSnackbar
        color={snackbar.color}
        icon={snackbar.color === "success" ? "check" : "warning"}
        title={snackbar.color === "success" ? "Úspech" : "Chyba"}
        content={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
        close={closeSnackbar}
        bgWhite
        dateTime={new Date().toLocaleTimeString()}
      />
    </DashboardLayout>
  );
}

export default Documentation;
